import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge, Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner, StatusBadge,
} from '../components/ui';
import { date, shares } from '../lib/format';

export default function Stakeholders() {
  const { companyId, role } = useCompany();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // the stakeholder row being edited
  const [flash, setFlash] = useState(null);

  const list = useAsync(
    () => api.stakeholders(companyId, search ? { search } : undefined),
    [companyId, search]
  );

  const canEdit = ['LEGAL', 'ADMIN', 'OWNER'].includes(role);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Stakeholders</h1>
          <p>
            Anyone who can hold a security — founders, employees, funds, SPVs, and the pool
            reserve. Separate from login accounts: most stakeholders never sign in.
          </p>
        </div>
        <div className="page-actions">
          <Input
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          {canEdit ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              Add stakeholder
            </Button>
          ) : null}
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />

      {list.loading ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBanner error={list.error} onRetry={list.reload} />
      ) : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.id}
            empty="No stakeholders yet."
            columns={[
              {
                key: 'name',
                header: 'Name',
                render: (r) => (
                  <span className="primary-cell">
                    {r.name} {r.kind === 'ENTITY' ? <Badge tone="neutral">entity</Badge> : null}
                  </span>
                ),
              },
              { key: 'email', header: 'Email', render: (r) => r.email ?? '—' },
              { key: 'title', header: 'Title', render: (r) => r.title ?? '—' },
              { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              {
                key: 'stock',
                header: 'Stock',
                align: 'right',
                numeric: true,
                render: (r) => shares(r.stock_outstanding),
              },
              {
                key: 'awards',
                header: 'Awards',
                align: 'right',
                numeric: true,
                render: (r) => shares(r.awards_outstanding),
              },
              {
                key: 'live',
                header: 'Securities',
                align: 'right',
                numeric: true,
                render: (r) => r.live_securities,
              },
              { key: 'start', header: 'Start', render: (r) => date(r.start_date) },
              {
                key: 'actions', header: '',
                render: (r) => canEdit ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
                ) : null,
              },
            ]}
            rows={list.data}
          />
        </Card>
      )}

      {creating ? (
        <StakeholderModal
          companyId={companyId}
          onClose={() => setCreating(false)}
          onSaved={(name) => {
            setCreating(false);
            setFlash(`Added ${name}.`);
            list.reload();
          }}
        />
      ) : null}
      {editing ? (
        <EditStakeholderModal
          companyId={companyId}
          stakeholder={editing}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            setFlash(`Updated ${name}.`);
            list.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function StakeholderModal({ companyId, onClose, onSaved }) {
  const [form, setForm] = useState({
    kind: 'INDIVIDUAL',
    name: '',
    email: '',
    title: '',
    startDate: '',
    employeeRef: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      await api.createStakeholder(companyId, body);
      onSaved(form.name);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const fieldErrors = error?.fieldErrors ?? {};

  return (
    <Modal
      title="Add stakeholder"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Add
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Kind">
            <Select value={form.kind} onChange={set('kind')}>
              <option value="INDIVIDUAL">Individual</option>
              <option value="ENTITY">Entity (fund, SPV, company)</option>
            </Select>
          </Field>
          <Field label="Name" required error={fieldErrors.name}>
            <Input value={form.name} onChange={set('name')} required autoFocus />
          </Field>
          <Field
            label="Email"
            hint="Unique per company. Links this holder to a login when they sign in."
            error={fieldErrors.email}
          >
            <Input type="email" value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Title">
            <Input value={form.title} onChange={set('title')} />
          </Field>
          <Field label="Start date" error={fieldErrors.startDate}>
            <Input type="date" value={form.startDate} onChange={set('startDate')} />
          </Field>
          <Field label="Employee ref" hint="Your HR system's identifier">
            <Input value={form.employeeRef} onChange={set('employeeRef')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Edit a stakeholder already on file.
 *
 * `status: TERMINATED` is the one field here with no other path to it at
 * all -- stakeholders.remove() itself refuses to delete anyone who holds or
 * has held securities and says to set status to TERMINATED instead, and
 * until this modal existed nothing in the product could actually do that.
 *
 * Tax ID is write-only, deliberately never prefilled: every read of it
 * (including this same stakeholder's own row a moment ago) comes back
 * masked, and prefilling a masked value would silently overwrite the real
 * one with asterisks the instant this form saved.
 */
function EditStakeholderModal({ companyId, stakeholder, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: stakeholder.name ?? '',
    email: stakeholder.email ?? '',
    title: stakeholder.title ?? '',
    startDate: stakeholder.start_date ? String(stakeholder.start_date).slice(0, 10) : '',
    endDate: stakeholder.end_date ? String(stakeholder.end_date).slice(0, 10) : '',
    status: stakeholder.status ?? 'ACTIVE',
    notes: stakeholder.notes ?? '',
    taxId: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Fields the schema declares optional (not nullable) are only sent
      // when non-blank — omitting is how this API's PATCH routes express
      // "leave it alone," not an explicit null.
      const body = { name: form.name, status: form.status };
      if (form.email) body.email = form.email;
      if (form.title) body.title = form.title;
      if (form.startDate) body.start_date = form.startDate;
      if (form.endDate) body.end_date = form.endDate;
      if (form.notes) body.notes = form.notes;
      if (form.taxId) body.tax_id = form.taxId;
      await api.updateStakeholder(companyId, stakeholder.id, body);
      onSaved(form.name);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const fieldErrors = error?.fieldErrors ?? {};

  return (
    <Modal
      title={`Edit ${stakeholder.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>Save</Button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Name" required error={fieldErrors.name}>
            <Input value={form.name} onChange={set('name')} required autoFocus />
          </Field>
          <Field label="Email" error={fieldErrors.email}>
            <Input type="email" value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Title">
            <Input value={form.title} onChange={set('title')} />
          </Field>
          <Field label="Status" hint="TERMINATED is the only way to offboard someone who holds or has held securities — they cannot be deleted outright">
            <Select value={form.status} onChange={set('status')}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="TERMINATED">Terminated</option>
            </Select>
          </Field>
          <Field label="Start date" error={fieldErrors.start_date}>
            <Input type="date" value={form.startDate} onChange={set('startDate')} />
          </Field>
          <Field label="End date" error={fieldErrors.end_date}>
            <Input type="date" value={form.endDate} onChange={set('endDate')} />
          </Field>
          <Field
            label="Tax ID (SSN/EIN)"
            hint="Write-only — every read of this comes back masked, so it is never shown here. Leave blank to keep the current value."
          >
            <Input value={form.taxId} onChange={set('taxId')} placeholder="Leave blank to keep unchanged" />
          </Field>
          <Field label="Notes">
            <Input value={form.notes} onChange={set('notes')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
